// apps/provider/src/services/api/clinical.js
// Encounter, clinical note, vitals, lab order, case, and intervention catalog methods
//
// Methods: createEncounter, getEncounters, getEncounter, updateEncounter,
//          mergeBillingCodes, deleteEncounter, updateEncounterStatus, createClinicalNote,
//          getClinicalNotes, getClinicalNote, updateClinicalNote,
//          signClinicalNote, addClinicalNoteAddendum, getEncountersByPatient,
//          saveVitals, getEncounterRecordings, getLabOrders,
//          updateLabOrder, getCases, getCase, createCase, updateCase, deleteCase,
//          getInterventionCatalog, createInterventionCatalogItem,
//          updateInterventionCatalogItem, deleteInterventionCatalogItem
//
// Response types: @prism-ehr/shared → Encounter, ClinicalNote, LabOrder, Case

/**
 * @returns {Object} Clinical domain methods (bound to `this` of the composed apiService)
 */
export function createClinicalMethods() {
  return {
    // ============ Encounters ============

    /**
     * Create a new encounter.
     *
     * Xano: POST /encounter
     * Xano file: 636_encounter_POST.xs
     *
     * @param {Object} encounterData
     * @returns {Promise<import('@prism-ehr/shared').Encounter>}
     */
    async createEncounter(encounterData) {
      return this.request('/encounter', {
        method: 'POST',
        body: JSON.stringify(encounterData)
      }, 'createEncounter');
    },

    /**
     * Atomically create an encounter and clinical note together.
     * Returns both the encounter and clinical_note records.
     *
     * Xano: POST /visit
     * Xano file: visit_POST.xs
     *
     * @param {Object} visitData - Combined encounter + note fields
     * @param {string} visitData.patient_id
     * @param {string} visitData.provider_id
     * @param {string} visitData.organization_id
     * @param {number} [visitData.encounter_date] - Epoch ms
     * @param {boolean} [visitData.exclude_from_billing] - Stamps invoice_id/insurance_claim_id = "external" so the encounter skips the ready-to-bill queue
     * @param {string} [visitData.encounter_type]
     * @param {string} [visitData.appointment_id]
     * @param {string} [visitData.note_type]
     * @param {string} [visitData.template_id]
     * @param {number} [visitData.case_id]
     * @param {string[]} [visitData.body_regions]
     * @returns {Promise<{ encounter: import('@prism-ehr/shared').Encounter, clinical_note: import('@prism-ehr/shared').ClinicalNote }>}
     */
    async createVisit(visitData) {
      return this.request('/visit', {
        method: 'POST',
        body: JSON.stringify(visitData)
      }, 'createVisit');
    },

    /**
     * Get encounters with filters.
     *
     * Xano: GET /encounter
     * Xano file: 635_encounter_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.location_id]
     * @param {string} [filters.status]
     * @param {string} [filters.encounter_type]
     * @param {string} [filters.start_date]
     * @param {string} [filters.end_date]
     * @param {number} [filters.case_id]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').Encounter[]>}
     */
    async getEncounters(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.encounter_type) queryParams.append('encounter_type', filters.encounter_type);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.case_id) queryParams.append('case_id', filters.case_id);
      if (filters.case_ids) queryParams.append('case_ids', filters.case_ids);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.query) queryParams.append('query', filters.query);
      if (filters.external) queryParams.append('external', JSON.stringify(filters.external));
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);
      if (filters.clinical_review_needed != null) queryParams.append('clinical_review_needed', filters.clinical_review_needed);
      if (filters.exclude_status) queryParams.append('exclude_status', filters.exclude_status);

      return this.request(`/encounter?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    async getUnbilledEncounters(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.provider_ids) queryParams.append('provider_ids', filters.provider_ids);
      else if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.encounter_type) queryParams.append('encounter_type', filters.encounter_type);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.case_id) queryParams.append('case_id', filters.case_id);
      if (filters.case_ids) queryParams.append('case_ids', filters.case_ids);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.query) queryParams.append('query', filters.query);
      if (filters.external) queryParams.append('external', JSON.stringify(filters.external));
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);
      if (filters.clinical_review_needed != null) queryParams.append('clinical_review_needed', filters.clinical_review_needed);
      if (filters.exclude_pending_review != null) queryParams.append('exclude_pending_review', filters.exclude_pending_review);
      if (filters.exclude_pending_cosign != null) queryParams.append('exclude_pending_cosign', filters.exclude_pending_cosign);

      return this.request(`/encounter-dev?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single encounter by ID.
     *
     * Xano: GET /encounter/{id}
     * Xano file: 634_encounter_encounter_id_GET.xs
     *
     * @param {string} id
     * @returns {Promise<import('@prism-ehr/shared').Encounter>}
     */
    async getEncounter(id) {
      return this.request(`/encounter/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Update an encounter.
     *
     * Xano: PATCH /encounter/{id}
     * Xano file: 637_encounter_encounter_id_PATCH.xs
     *
     * @param {string} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').Encounter>}
     */
    async updateEncounter(id, data) {
      return this.request(`/encounter/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'updateEncounter');
    },

    /**
     * Merge a note's billing codes into encounter.billing_codes (server-side atomic merge).
     *
     * Xano: PATCH /encounter-merge-billing-codes/{id}
     * Xano file: encounter-merge-billing-codes_encounter_id_PATCH.xs
     *
     * @param {string} encounterId
     * @param {{ note_id: string, provider_id: string, icd10_codes?: Array, cpt_codes?: Array }} data
     * @returns {Promise<import('@prism-ehr/shared').Encounter>}
     */
    async mergeBillingCodes(encounterId, data) {
      return this.request(`/encounter-merge-billing-codes/${encounterId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'mergeBillingCodes');
    },

    /**
     * Delete an encounter.
     *
     * Xano: DELETE /encounter/{id}
     * Xano file: 633_encounter_encounter_id_DELETE.xs
     *
     * @param {string} id
     * @returns {Promise<null>}
     */
    async deleteEncounter(id) {
      return this.request(`/encounter/${id}`, {
        method: 'DELETE'
      }, 'deleteEncounter');
    },

    /**
     * Update an encounter's status.
     *
     * Xano: PATCH /encounter/{id}/status
     * Xano file: 733_encounter_encounter_id_status_PATCH.xs
     *
     * @param {string} id
     * @param {string} status
     * @returns {Promise<import('@prism-ehr/shared').Encounter>}
     */
    async updateEncounterStatus(id, status) {
      return this.request(`/encounter/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }, 'updateEncounterStatus');
    },

    // ============ Clinical Notes ============

    /**
     * Create a new clinical note.
     *
     * Xano: POST /clinical_note
     * Xano file: 641_clinical_note_POST.xs
     *
     * @param {Object} noteData
     * @returns {Promise<import('@prism-ehr/shared').ClinicalNote>}
     */
    async createClinicalNote(noteData) {
      return this.request('/clinical_note', {
        method: 'POST',
        body: JSON.stringify(noteData)
      }, 'createClinicalNote');
    },

    /**
     * Get clinical notes with filters.
     *
     * Xano: GET /clinical_note
     * Xano file: 640_clinical_note_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.encounter_id]
     * @param {string} [filters.note_type]
     * @param {number} [filters.case_id]
     * @param {boolean} [filters.signed]
     * @param {string} [filters.start_date]
     * @param {string} [filters.end_date]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').ClinicalNote[]>}
     */
    async getDocumentationReview(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.item_type) queryParams.append('item_type', filters.item_type);
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.per_page) queryParams.append('per_page', filters.per_page);
      return this.request(`/documentation-review?${queryParams.toString()}`, { method: 'GET' });
    },

    async getClinicalNotes(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.encounter_id) queryParams.append('encounter_id', filters.encounter_id);
      if (filters.note_type) queryParams.append('note_type', filters.note_type);
      if (filters.case_id) queryParams.append('case_id', filters.case_id);
      if (filters.case_ids) queryParams.append('case_ids', filters.case_ids);
      if (filters.note_types) queryParams.append('note_types', filters.note_types);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.signed !== undefined) queryParams.append('signed', String(filters.signed));
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.query) queryParams.append('query', filters.query);

      return this.request(`/clinical_note?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single clinical note by ID.
     *
     * Xano: GET /clinical_note/{id}
     * Xano file: 639_clinical_note_clinical_note_id_GET.xs
     *
     * @param {string} id
     * @returns {Promise<import('@prism-ehr/shared').ClinicalNote>}
     */
    async getClinicalNote(id) {
      return this.request(`/clinical_note/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Update a clinical note.
     *
     * Xano: PATCH /clinical_note/{id}
     * Xano file: 642_clinical_note_clinical_note_id_PATCH.xs
     *
     * @param {string} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ClinicalNote>}
     */
    async updateClinicalNote(id, data) {
      return this.request(`/clinical_note/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'updateClinicalNote');
    },

    /**
     * Sign a clinical note with attestation.
     *
     * Xano: PATCH /clinical_note_sign/{id}
     * Xano file: 734_clinical_note_clinical_note_id_sign_PATCH.xs
     *
     * @param {string} id
     * @param {Object} options - Signature, optional case-discharge request, optional non_billed (0-unit visit skips billing).
     * @returns {Promise<import('@prism-ehr/shared/types/api/clinical').ClinicalNoteSignResponse>}
     */
    async signClinicalNote(id, { signature, attestation, discharge_case = false, non_billed = false } = {}) {
      return this.request(`/clinical_note_sign/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          signature: signature || '',
          attestation: attestation || 'I attest that this clinical note is accurate and complete to the best of my knowledge.',
          signed_at: new Date().toISOString(),
          discharge_case,
          // All CPT lines at 0 units and the therapist confirmed: the endpoint
          // creates a "removed" hold so the visit skips Ready to Bill
          non_billed,
        })
      }, 'signClinicalNote');
    },

    /**
     * Add an addendum to a clinical note.
     *
     * Xano: POST /clinical_note/{id}/addendum
     * Xano file: not found in staging
     *
     * @param {string} id
     * @param {Object} addendum
     * @returns {Promise<import('@prism-ehr/shared').ClinicalNote>}
     */
    /**
     * Cosign a clinical note as supervisor.
     *
     * Xano: PATCH /clinical_note_cosign/{id}
     * Xano file: clinical_note_clinical_note_id_cosign_PATCH.xs
     *
     * @param {string} id
     * @param {Object} data
     * @param {string} data.cosignature_attestation
     * @returns {Promise<import('@prism-ehr/shared').ClinicalNote>}
     */
    async cosignClinicalNote(id, data) {
      return this.request(`/clinical_note_cosign/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'cosignClinicalNote');
    },

    /**
     * Get all clinical notes for the supervision queue.
     * Returns all notes — caller splits into cosign vs. review buckets client-side.
     * Avoids boolean string-to-bool coercion issues with Xano ==? on bool fields.
     *
     * @param {Object} [filters]
     * @param {string} [filters.organization_id]
     * @returns {Promise<Object>} Paginated list of clinical notes
     */
    async getSupervisionQueueNotes(superviseeIds = []) {
      const ids = superviseeIds.join(',');
      return this.request(`/clinical-note-supervision-queue?supervisee_ids=${ids}&limit=200`, {
        method: 'GET'
      }, 'getSupervisionQueueNotes');
    },

    /**
     * Assign (or clear) the designated signer on a draft note.
     * Dedicated endpoint — the generic PATCH strips nulls so it can't clear.
     *
     * Xano: PATCH /clinical-note-assign/:id
     *
     * @param {string} noteId
     * @param {string|null} assignedSignerId - null clears the assignment
     * @returns {Promise<import('@prism-ehr/shared').ClinicalNote>}
     */
    async assignClinicalNoteSigner(noteId, assignedSignerId) {
      return this.request(`/clinical-note-assign/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ assigned_signer_id: assignedSignerId })
      }, 'assignClinicalNoteSigner');
    },

    /**
     * Unsigned notes assigned to the current user for signature
     * (credentialing handoff queue).
     *
     * Xano: GET /clinical-notes-assigned
     *
     * @returns {Promise<Object>} Paginated list of clinical notes with addons
     */
    async getAssignedNotes() {
      return this.request('/clinical-notes-assigned?limit=200', {
        method: 'GET'
      }, 'getAssignedNotes');
    },

    async addClinicalNoteAddendum(id, addendum, encounterId = null) {
      const note = await this.getClinicalNote(id);
      const existingAddendums = note.addendums || [];

      const newAddendum = {
        id: crypto.randomUUID(),
        content: addendum.content,
        reason: addendum.reason || null,
        created_by: addendum.created_by,
        created_at: new Date().toISOString(),
        signed: false,
        ...(addendum.code_corrections && { code_corrections: addendum.code_corrections }),
      };

      const noteUpdate = { addendums: [...existingAddendums, newAddendum] };

      if (addendum.code_corrections) {
        if (addendum.code_corrections.icd10_codes) {
          noteUpdate.icd10_codes = addendum.code_corrections.icd10_codes;
        }
        if (addendum.code_corrections.cpt_codes) {
          noteUpdate.cpt_codes = addendum.code_corrections.cpt_codes;

          if (note.sections_data?.sections) {
            const cloned = JSON.parse(JSON.stringify(note.sections_data));
            for (const section of cloned.sections) {
              for (const field of section.fields || []) {
                if (Array.isArray(field.value) && field.value.length > 0 && field.value[0]?.code) {
                  field.value = addendum.code_corrections.cpt_codes;
                }
              }
            }
            noteUpdate.sections_data = cloned;
          }
        }
      }

      const updatedNote = await this.updateClinicalNote(id, noteUpdate);

      if (addendum.code_corrections && encounterId) {
        const renderingProviderId = (note.treating_provider_id && note.treating_provider_id !== '') ? note.treating_provider_id : note.provider_id;
        await this.mergeBillingCodes(encounterId, {
          note_id: id,
          provider_id: renderingProviderId,
          icd10_codes: addendum.code_corrections.icd10_codes || [],
          cpt_codes: addendum.code_corrections.cpt_codes || [],
        });
      }

      return updatedNote;
    },

    /**
     * Delete a clinical note (unsigned only).
     *
     * Xano: DELETE /clinical_note/{id}
     * Xano file: clinical_note_clinical_note_id_DELETE.xs
     *
     * @param {string} id
     * @returns {Promise<null>}
     */
    async deleteClinicalNote(id) {
      return this.request(`/clinical_note/${id}`, {
        method: 'DELETE'
      }, 'deleteClinicalNote');
    },

    // ============ Convenience Wrappers ============

    /**
     * Get encounters for a specific patient, optionally filtered by status.
     *
     * Convenience wrapper around getEncounters.
     *
     * @param {string} patientId
     * @param {string} [status]
     * @returns {Promise<import('@prism-ehr/shared').Encounter[]>}
     */
    async getEncountersByPatient(patientId, status = null) {
      const filters = { patient_id: patientId };
      if (status) filters.status = status;
      return this.getEncounters(filters);
    },

    // ============ Vitals ============

    /**
     * Save vitals for an encounter.
     *
     * Xano: POST /encounter/{encounterId}/vitals
     * Xano file: not found in staging (may be handled by encounter PATCH)
     *
     * @param {string} encounterId
     * @param {Object} vitals
     * @returns {Promise<Object>}
     */
    async saveVitals(encounterId, vitals) {
      return this.request(`/encounter/${encounterId}/vitals`, {
        method: 'POST',
        body: JSON.stringify(vitals)
      }, 'saveVitals');
    },

    // ============ Encounter Recordings ============

    /**
     * Get recordings for an encounter.
     *
     * Xano: GET /encounter/{encounterId}/recordings
     * Xano file: 935_encounter_encounter_id_recordings_GET.xs
     *
     * @param {string} encounterId
     * @returns {Promise<Object[]>}
     */
    async getEncounterRecordings(encounterId) {
      return this.request(`/encounter/${encounterId}/recordings`, {
        method: 'GET'
      });
    },

    // ============ Lab Orders ============

    /**
     * Get lab orders for an organization, optionally filtered by patient.
     *
     * Xano: GET /lab_order
     * Xano file: 1000_lab_order_GET.xs
     *
     * @param {string} organizationId
     * @param {string} [patientId]
     * @returns {Promise<import('@prism-ehr/shared').LabOrder[]>}
     */
    async getLabOrders(organizationId, patientId = null) {
      const params = new URLSearchParams({ organization_id: organizationId });
      if (patientId) params.append('patient_id', patientId);
      return this.request(`/lab_order?${params.toString()}`, { method: 'GET' }, 'getLabOrders');
    },

    /**
     * Update a lab order.
     *
     * Xano: PATCH /lab_order/{id}
     *
     * @param {string} labOrderId
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').LabOrder>}
     */
    async updateLabOrder(labOrderId, updates) {
      return this.request(`/lab_order/${labOrderId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      }, 'updateLabOrder');
    },

    // ============ Cases ============

    /**
     * Get cases with filters.
     *
     * Xano: GET /case
     * Xano file: case_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.status]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').Case[]>}
     */
    async getCases(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);
      if (filters.case_ids) queryParams.append('case_ids', filters.case_ids);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      return this.request(`/case?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single case by ID.
     *
     * Xano: GET /case/{id}
     * Xano file: case_case_id_GET.xs
     *
     * @param {number} id
     * @returns {Promise<import('@prism-ehr/shared').Case>}
     */
    async getCase(id) {
      return this.request(`/case/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new case.
     *
     * Xano: POST /case
     * Xano file: case_POST.xs
     *
     * @param {Object} caseData
     * @returns {Promise<import('@prism-ehr/shared').Case>}
     */
    /**
     * Delete all clinical data for the org's designated test patient (rows in
     * clinical_note, encounter, appointment, case, plan_of_care(+versions),
     * flow_sheet_entry, encounter_hold). Server-side guarded: the patient must
     * be organization.settings.test_patient_id. The patient row is kept.
     *
     * Xano: POST /test-patient-reset (test-patient-reset_POST.xs, both envs)
     *
     * @param {string} patientId
     * @returns {Promise<{notes: number, encounters: number, appointments: number, cases: number, plans_of_care: number, flow_sheet_entries: number, encounter_holds: number}>}
     */
    resetTestPatient(patientId) {
      return this.request('/test-patient-reset', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId })
      }, 'resetTestPatient');
    },

    async createCase(caseData) {
      return this.request('/case', {
        method: 'POST',
        body: JSON.stringify(caseData)
      }, 'createCase');
    },

    /**
     * Update a case.
     *
     * Xano: PATCH /case/{id}
     * Xano file: case_case_id_PATCH.xs
     *
     * @param {number} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').Case>}
     */
    async updateCase(id, data) {
      return this.request(`/case/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'updateCase');
    },

    /**
     * Delete a case.
     *
     * Xano: DELETE /case/{id}
     * Xano file: case_case_id_DELETE.xs
     *
     * @param {number} id
     * @returns {Promise<null>}
     */
    async deleteCase(id) {
      return this.request(`/case/${id}`, {
        method: 'DELETE'
      }, 'deleteCase');
    },

    // ============ Intervention Catalog ============

    /**
     * Get intervention catalog items for the current org.
     *
     * Xano: GET /intervention-catalog
     * Xano file: intervention-catalog_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.category]
     * @param {string} [filters.clinical_specialty]
     * @param {boolean} [filters.is_active]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @param {string} [filters.search]
     * @returns {Promise<Object>}
     */
    async getInterventionCatalog(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.category) queryParams.append('category', filters.category);
      if (filters.clinical_specialty) queryParams.append('clinical_specialty', filters.clinical_specialty);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.search) queryParams.append('search', filters.search);

      return this.request(`/intervention-catalog?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Create an intervention catalog item.
     *
     * Xano: POST /intervention-catalog
     * Xano file: intervention-catalog_POST.xs
     *
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async createInterventionCatalogItem(data) {
      return this.request('/intervention-catalog', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'createInterventionCatalogItem');
    },

    /**
     * Update an intervention catalog item.
     *
     * Xano: PATCH /intervention-catalog/{id}
     * Xano file: intervention-catalog_{intervention_catalog_id}_PATCH.xs
     *
     * @param {string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async updateInterventionCatalogItem(id, data) {
      return this.request(`/intervention-catalog/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'updateInterventionCatalogItem');
    },

    /**
     * Soft-delete an intervention catalog item (sets is_active=false).
     *
     * Xano: DELETE /intervention-catalog/{id}
     * Xano file: intervention-catalog_{intervention_catalog_id}_DELETE.xs
     *
     * @param {string} id
     * @returns {Promise<null>}
     */
    async deleteInterventionCatalogItem(id) {
      return this.request(`/intervention-catalog/${id}`, {
        method: 'DELETE'
      }, 'deleteInterventionCatalogItem');
    },

    /**
     * Get paginated encounters pending clinical review.
     *
     * Xano: GET /clinical-review-encounters
     * Xano file: clinical-review-encounters_GET.xs
     *
     * @param {Object} [filters]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.query] - Patient name full-text search
     * @returns {Promise<{items: import('@prism-ehr/shared').Encounter[], itemsTotal: number, pageTotal: number, curPage: number, perPage: number}>}
     */
    async getClinicalReviewEncounters(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.query) queryParams.append('query', filters.query);
      return this.request(`/clinical-review-encounters?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    // ============ User Smart Text ============

    async getUserSmartText(fieldIds, fieldLabels) {
      const params = new URLSearchParams();
      if (fieldIds?.length) params.append('field_ids', JSON.stringify(fieldIds));
      if (fieldLabels?.length) params.append('field_labels', JSON.stringify(fieldLabels));
      return this.request(`/user-smart-text?${params.toString()}`, {}, 'getUserSmartText');
    },

    async createUserSmartText(data) {
      return this.request('/user-smart-text', {
        method: 'POST',
        body: JSON.stringify(data),
      }, 'createUserSmartText');
    },

    async updateUserSmartText(id, data) {
      return this.request(`/user-smart-text/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }, 'updateUserSmartText');
    },

    async deleteUserSmartText(id) {
      return this.request(`/user-smart-text/${id}`, {
        method: 'DELETE',
      }, 'deleteUserSmartText');
    },
  };
}
